import { useEffect, RefObject } from 'react';

interface UseDisableZoomOptions {
    /**
     * Whether the zoom prevention should be active
     * @default true
     */
    enabled?: boolean;
    /**
     * Optional ref to limit zoom prevention to a specific element
     * If not provided, applies to the entire document
     */
    targetRef?: RefObject<HTMLElement>;
}

export const useDisableZoom = (options: UseDisableZoomOptions = {}) => {
    const { enabled = true, targetRef } = options;

    useEffect(() => {
        if (!enabled) return;

        const target = targetRef?.current || document;

        // Prevent trackpad pinch zoom (wheel event with ctrlKey)
        const preventZoom = (e: WheelEvent) => {
            if (e.ctrlKey) {
                e.preventDefault();
            }
        };

        // Prevent touch pinch zoom (2+ fingers)
        const preventTouchZoom = (e: TouchEvent) => {
            if (e.touches.length > 1) {
                e.preventDefault();
            }
        };

        // Prevent gesture events (Safari)
        const preventGesture = (e: Event) => {
            e.preventDefault();
        };

        // Must use {passive: false} to allow preventDefault
        target.addEventListener('wheel', preventZoom as EventListener, { passive: false });
        target.addEventListener('touchmove', preventTouchZoom as EventListener, { passive: false });
        target.addEventListener('gesturestart', preventGesture, { passive: false });
        target.addEventListener('gesturechange', preventGesture, { passive: false });
        target.addEventListener('gestureend', preventGesture, { passive: false });

        return () => {
            target.removeEventListener('wheel', preventZoom as EventListener);
            target.removeEventListener('touchmove', preventTouchZoom as EventListener);
            target.removeEventListener('gesturestart', preventGesture);
            target.removeEventListener('gesturechange', preventGesture);
            target.removeEventListener('gestureend', preventGesture);
        };
    }, [enabled, targetRef]);
};
